import type { SyncProvider } from './syncProvider.js';
import { REMOTE_CHECKPOINTS_PREFIX, SCHEMA_VERSION, type SyncResult } from './types.js';
import { storage } from '../shell/storage.js';
import { getOrCreateDeviceId } from '../shell/deviceId.js';
import type { ModuleRecord } from '../shell/types.js';

/**
 * Rolling point-in-time backups ("checkpoints") on the sync provider.
 *
 * This is an ADDITIVE history layer, deliberately decoupled from the sync
 * engine: checkpoints live under shell/checkpoints/ which the reconciler never
 * reads, so they cannot affect last-write-wins. A checkpoint captures every
 * local module's code (files) + KV data at a moment in time. Restoring (Phase
 * 3) writes a chosen snapshot back as a fresh "current" write, so normal LWW
 * propagates it — timestamps are never rewound.
 *
 * Binaries (audio/images) are not captured yet — only code + KV data.
 */

export interface CheckpointModule {
  name: string;
  icon: string | null;
  files: Record<string, string>;
  data: Record<string, string>;
}

export interface Checkpoint {
  schemaVersion: number;
  createdAtMs: number;
  deviceId: string;
  modules: Record<string, CheckpointModule>;
}

export interface CheckpointMeta {
  path: string;
  createdAtMs: number;
  deviceId: string;
  sizeBytes: number;
}

const KEEP_KEY = 'shell.checkpointKeep';
const ENABLED_KEY = 'shell.checkpointEnabled';
const DEFAULT_KEEP = 10;

export function getCheckpointKeep(): number {
  const n = Number(localStorage.getItem(KEEP_KEY));
  if (!Number.isFinite(n) || n < 1) return DEFAULT_KEEP;
  return Math.min(50, Math.floor(n));
}
export function setCheckpointKeep(n: number): void {
  localStorage.setItem(KEEP_KEY, String(Math.min(50, Math.max(1, Math.floor(n)))));
}
export function isCheckpointingEnabled(): boolean {
  return localStorage.getItem(ENABLED_KEY) !== '0';
}
export function setCheckpointingEnabled(on: boolean): void {
  localStorage.setItem(ENABLED_KEY, on ? '1' : '0');
}

/**
 * Captures the synced modules (code + KV data) at this moment.
 *
 * Only modules opted into sync are captured: a checkpoint lives on Drive, so
 * including a `local_only` module would contradict the user's choice to keep it
 * off the cloud — and it would surface on other devices as a phantom the
 * Timeline can't make sense of.
 */
export function buildCheckpoint(): Checkpoint {
  const modules: Record<string, CheckpointModule> = {};
  for (const [id, recRaw] of storage.listModules() as Array<[string, ModuleRecord]>) {
    if (id.startsWith('__')) continue;
    const rec = recRaw;
    if (rec.dataSyncMode !== 'sync' && rec.codeSyncMode !== 'sync') continue;
    const data: Record<string, string> = {};
    for (const key of storage.listKeysForModule(id)) {
      if (key.startsWith('__published__::') || key.startsWith('.')) continue;
      data[key] = storage.get({ moduleId: id, key });
    }
    modules[id] = {
      name: rec.name,
      icon: rec.icon ?? null,
      files: { ...(rec.files ?? {}), 'index.html': rec.htmlSource },
      data,
    };
  }
  return { schemaVersion: SCHEMA_VERSION, createdAtMs: Date.now(), deviceId: getOrCreateDeviceId(), modules };
}

/** Stable signature of a checkpoint's content (ignores time/device) for dedupe. */
function signature(cp: Checkpoint): string {
  return JSON.stringify(cp.modules);
}

function metaFromPath(path: string, sizeBytes: number): CheckpointMeta | null {
  const file = path.slice(path.lastIndexOf('/') + 1);
  const m = file.match(/^(\d+)-(.+)\.json$/);
  if (!m) return null;
  return { path, createdAtMs: Number(m[1]), deviceId: m[2], sizeBytes };
}

/** Lists checkpoints, newest first. */
export async function listCheckpoints(provider: SyncProvider): Promise<CheckpointMeta[]> {
  const files = await provider.listFiles(`${REMOTE_CHECKPOINTS_PREFIX}/`);
  return files
    .map((f) => metaFromPath(f.path, f.sizeBytes))
    .filter((m): m is CheckpointMeta => m !== null)
    .sort((a, b) => b.createdAtMs - a.createdAtMs);
}

export async function readCheckpoint(provider: SyncProvider, path: string): Promise<Checkpoint | null> {
  const json = await provider.readFile(path);
  if (!json) return null;
  try { return JSON.parse(json) as Checkpoint; } catch { return null; }
}

export interface RestoreSummary {
  modulesRestored: number;
  keysWritten: number;
}

/**
 * Applies one module from a checkpoint to local storage, writing-as-newest:
 * the code gets a fresh `lastModifiedMs` and every data key is re-set with a
 * fresh timestamp, so normal last-write-wins propagates the recovered state to
 * other devices on the next sync. Timestamps are never rewound.
 *
 * Recovery is itself reversible: the current (pre-recovery) code is archived
 * into files['revisions/…'] first — same mechanism as importModule — so the
 * version being replaced stays in History.
 *
 * Data is overwritten additively: keys present in the checkpoint are restored,
 * but keys the module has gained *since* the checkpoint are left untouched (we
 * never delete live data during a recovery).
 */
function applyModuleRestore(id: string, m: CheckpointModule): number {
  const existing = storage.getModule(id) as ModuleRecord | undefined;
  const restoredHtml = m.files['index.html'] ?? existing?.htmlSource ?? '';

  // Start from the checkpoint's file map (which already carries any historical
  // revisions/ entries captured at checkpoint time).
  const files: Record<string, string> = { ...m.files };
  // Carry forward revisions the module accumulated after the checkpoint, and
  // archive the current code so this recovery can itself be undone.
  if (existing) {
    for (const [p, c] of Object.entries(existing.files ?? {})) {
      if (p.startsWith('revisions/')) files[p] = c;
    }
    const isPlaceholder = existing.htmlSource.length < 200 && existing.htmlSource.includes('<p>');
    if (existing.htmlSource && existing.htmlSource !== restoredHtml && !isPlaceholder) {
      files[`revisions/index_${existing.lastModifiedMs || Date.now()}.html`] = existing.htmlSource;
    }
  }

  const now = Date.now();
  const rec: ModuleRecord = {
    id,
    name: m.name,
    icon: m.icon ?? undefined,
    htmlSource: restoredHtml,
    files,
    lastModifiedMs: now,
    lastDataSyncMs: existing?.lastDataSyncMs ?? 0,
    lastCodeSyncMs: existing?.lastCodeSyncMs ?? 0,
    // Keep the user's sync prefs when the module still exists. A module being
    // recovered from deletion comes back fully synced so it actually reappears
    // on the other devices.
    dataSyncMode: existing?.dataSyncMode ?? 'sync',
    codeSyncMode: existing?.codeSyncMode ?? 'sync',
    backgroundRefresh: existing?.backgroundRefresh,
    stopOnLeave: existing?.stopOnLeave,
    manifest: existing?.manifest,
  };
  storage.putModule(id, rec);

  let keys = 0;
  for (const [key, value] of Object.entries(m.data ?? {})) {
    storage.set({ moduleId: id, key }, value); // bumps meta to now → write-as-newest
    keys++;
  }
  return keys;
}

/** Recovers a single module from a checkpoint. Returns false if absent. */
export function restoreModuleFromCheckpoint(cp: Checkpoint, moduleId: string): boolean {
  const m = cp.modules?.[moduleId];
  if (!m) return false;
  applyModuleRestore(moduleId, m);
  return true;
}

/** Recovers every module in a checkpoint. */
export function restoreWholeCheckpoint(cp: Checkpoint): RestoreSummary {
  let modulesRestored = 0;
  let keysWritten = 0;
  for (const [id, m] of Object.entries(cp.modules ?? {})) {
    keysWritten += applyModuleRestore(id, m);
    modulesRestored++;
  }
  return { modulesRestored, keysWritten };
}

/** Deletes the oldest checkpoints beyond `keep`. */
async function prune(provider: SyncProvider, keep: number): Promise<void> {
  const all = await listCheckpoints(provider);
  for (const cp of all.slice(keep)) {
    try { await provider.deleteFile(cp.path); } catch { /* best-effort */ }
  }
}

/**
 * Writes a checkpoint after a sync that changed something — unless the content
 * is identical to the latest checkpoint (dedupe). Then prunes to the retention
 * limit. Best-effort: never throws into the sync flow.
 */
export async function maybeWriteCheckpoint(provider: SyncProvider | null, result: SyncResult): Promise<boolean> {
  if (!provider || !provider.isAuthenticated() || !isCheckpointingEnabled()) return false;
  const changed = result.codeUploaded + result.codeDownloaded + result.codeDeleted +
    result.dataUploaded + result.dataDownloaded + result.dataDeleted > 0;
  if (!changed) return false;

  try {
    const cp = buildCheckpoint();
    if (!Object.keys(cp.modules).length) return false;

    // Dedupe against the most recent checkpoint.
    const recent = await listCheckpoints(provider);
    if (recent.length) {
      const last = await readCheckpoint(provider, recent[0]!.path);
      if (last && signature(last) === signature(cp)) return false;
    }

    const path = `${REMOTE_CHECKPOINTS_PREFIX}/${cp.createdAtMs}-${cp.deviceId}.json`;
    const ok = await provider.writeFile(path, JSON.stringify(cp));
    if (ok) await prune(provider, getCheckpointKeep());
    return ok;
  } catch (e) {
    console.warn('[checkpoints] write failed', e);
    return false;
  }
}
